import { useEffect, useState } from 'react';
import { PropTypes } from 'base/react-libs';
import { Modal, ModalHeaderCustom, ModalBody, Button } from '@evertrue/et-components';
import moment from 'moment';
import parsePhoneNumber from 'libphonenumber-js';
import { getContactCallHistory } from '../utils/call-history-requests';

/** Twilio quality tags that indicate a degraded call. */
const QUALITY_TAGS = ['high_jitter', 'high_packet_loss', 'high_rtt', 'low_mos'];

/**
 * Translate Twilio's quality metrics into a plain-English verdict for a non-technical user —
 * no "jitter"/"ms" jargon. Attributes a poor call to the caller's vs the recipient's side
 * using the per-edge metrics.
 */
const describeQuality = (insights) => {
  const tags = insights.tags || [];
  const isPoor = tags.some((tag) => QUALITY_TAGS.includes(tag)) || (insights.mos != null && insights.mos < 3.5);
  if (!isPoor) {
    return { label: 'Good', detail: '' };
  }
  const client = insights.client_jitter_ms;
  const carrier = insights.carrier_jitter_ms;
  if (client != null && (carrier == null || client >= carrier)) {
    return { label: 'Poor', detail: 'your internet connection was weak, which can cause choppy or dropped audio' };
  }
  if (carrier != null) {
    return { label: 'Poor', detail: "the recipient's connection was weak, which can cause choppy or dropped audio" };
  }
  return { label: 'Poor', detail: 'this can cause choppy or dropped audio' };
};

/** Format a call's timestamp (epoch seconds from the API) for display. */
const formatWhen = (call = {}) => {
  const timestamp = call.completed_at || call.created_at;
  return timestamp ? moment.unix(timestamp).format('MMM D, YYYY • h:mm A') : 'Unknown date';
};

const formatDuration = (seconds) => {
  if (!seconds) return '0s';
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return minutes ? `${minutes}m ${remainder}s` : `${remainder}s`;
};

/** Format the dialed number (E.164 from the API) for display, e.g. +15084688367 → (508) 468-8367. */
const formatDialedNumber = (e164) => {
  if (!e164) return null;
  try {
    const parsed = parsePhoneNumber(e164);
    return parsed ? parsed.formatNational() : e164;
  } catch (e) {
    return e164;
  }
};

/**
 * Modal showing the recent call history with a contact. Each call is enriched with live
 * Twilio Voice Insights (who hung up, quality) and a plain-English summary. Gated by the
 * `call.history` LaunchDarkly flag at the call site.
 */
const CallHistoryModal = ({ isOpen, onClose, contactId, contactName }) => {
  const [loading, setLoading] = useState(false);
  const [calls, setCalls] = useState([]);

  useEffect(() => {
    if (!isOpen || !contactId) return undefined;

    let cancelled = false;
    setLoading(true);
    getContactCallHistory(contactId).then((data) => {
      if (cancelled) return;
      setCalls(Array.isArray(data) ? data : []);
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [isOpen, contactId]);

  const renderBody = () => {
    if (loading) {
      return <p className="call-history--message">Loading call history…</p>;
    }
    if (!calls.length) {
      return <p className="call-history--message">No recent calls with this contact.</p>;
    }
    return (
      <ul className="call-history--list">
        {calls.map((entry) => {
          const call = entry.call || {};
          const insights = entry.insights || {};
          const dialedNumber = formatDialedNumber(entry.to_number);
          const quality = describeQuality(insights);
          const showQuality =
            insights.available &&
            (insights.mos != null || insights.client_jitter_ms != null || (insights.tags || []).length > 0);
          return (
            <li key={call.id} className="call-history--item">
              <div className="call-history--item-header">
                <span className="call-history--item-date">{formatWhen(call)}</span>
                <span className="call-history--item-separator"> — </span>
                <span className="call-history--item-duration">{formatDuration(call.duration_in_seconds)}</span>
                {dialedNumber && (
                  <>
                    <span className="call-history--item-separator"> — </span>
                    <span className="call-history--item-number">{dialedNumber}</span>
                  </>
                )}
              </div>
              <p className="call-history--item-summary">{entry.summary}</p>
              {showQuality && (
                <ul className={`call-history--quality-list ${quality.label === 'Poor' ? 'is-poor' : 'is-good'}`}>
                  <li>
                    Connection quality: {quality.label}
                    {quality.detail ? ` — ${quality.detail}` : ''}
                  </li>
                </ul>
              )}
              {entry.caller_id_note && (
                <p className="call-history--caller-id-note">
                  {entry.caller_id_note}{' '}
                  <a href="https://www.freecallerregistry.com/" target="_blank" rel="noopener noreferrer">
                    How to fix this
                  </a>
                </p>
              )}
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <Modal isOpen={isOpen} closeModal={onClose} size="large" closeOnOutsideClick={true}>
      <ModalHeaderCustom>
        <h3 className="call-history--title">Call History{contactName ? ` — ${contactName}` : ''}</h3>
        <Button type="simple" onClick={onClose}>
          Close
        </Button>
      </ModalHeaderCustom>
      <ModalBody>{renderBody()}</ModalBody>
    </Modal>
  );
};

CallHistoryModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  contactId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  contactName: PropTypes.string,
};

export default CallHistoryModal;
